import * as Analytics from 'js/analytics';
import breakpoints from 'js/breakpoints';
import {
    animateCSS,
    closest,
    createPubSub,
    extractSearchFormData,
    getComputedStyle,
    getViewport,
    parseHtmlToDom,
    serializeFormData
} from 'js/tools';
import validator from 'js/validator';
import { passesRequired, passesPattern, passesMaxLength } from 'js/form-utils';
import Recaptcha from 'js/utils/recaptcha';

const ATTR_FEEDBACK = 'data-feedback';

const CLASS_CTA_BUSY = 'cta--busy';
const CLASS_FIELD_WRAPPER = 'form-field-wrapper';
const CLASS_FILTER = 'filter-cloud__filter';

const JS_FORM_CANCEL = 'form-cancel';
const JS_FORM_OPEN = 'form-open';
const JS_FILTER_REMOVE = 'filter-remove';
const JS_FILTERS_CLEAR = 'filters-clear';
const JS_RESPONSE_CLOSE = 'response-close';

const SELECTOR_ACTIONS = '.filter-cloud__actions';
const SELECTOR_BTN_SAVE = '.filter-cloud__actions .cta-primary';
const SELECTOR_CANCEL = 'form .cta-dimmed';
const SELECTOR_DISTANCE = '[name="Distance"]';
const SELECTOR_EMAIL = '.input-text input';
const SELECTOR_FACETS_FORM = '.search-filters form';
const SELECTOR_FIELD_FEEDBACK = '.messages';
const SELECTOR_FORM = '.form';
const SELECTOR_KEYWORDS = '[name="Keywords"]';
const SELECTOR_LIST = '.filter-cloud__list';
const SELECTOR_MODEL_KEYWORDS = '[name="ModelKeywords"]';
const SELECTOR_POSTAL_CODE_CITY = '[name="PostalCodeCity"]';
const SELECTOR_RESPONSE_EMAIL = '.response-wrapper--email';
const SELECTOR_RESPONSE_ERROR = '.response-wrapper--error';
const SELECTOR_RESPONSE_SUCCESS = '.response-wrapper--success';
const SELECTOR_SUBMIT = '[type="submit"]';
const SELECTOR_WRAPPER = '.filter-cloud__wrapper';

export default class FilterCloud {
    constructor(node) {
        this.node = node;

        this.init();
    }

    init() {
        this.pubsub = createPubSub();

        this.declareNodes();
        this.bindEventListeners();

        if (this.formNode) {
            this.initForm();
            this.siteKey = window.viabovag?.googleRecaptchaSiteKey;
            this.recaptcha = new Recaptcha(this.formNode, this.siteKey);
        }

        if (this.responseEmailNode && !this.responseEmailNode.hidden) {
            setTimeout(() => {
                animateCSS({
                    node: this.responseEmailNode,
                    animationName: 'fadeOut',
                    callback: () => {
                        this.toggle(this.responseEmailNode, false);
                    }
                });
            }, 6000);
        }

        this.determineFilterContainerWidth();
    }

    declareNodes() {
        this.actionsNode = this.node.querySelector(SELECTOR_ACTIONS);
        this.btnSaveNode = this.node.querySelector(SELECTOR_BTN_SAVE);
        this.cancelNode = this.node.querySelector(SELECTOR_CANCEL);
        this.emailNode = this.node.querySelector(SELECTOR_EMAIL);
        this.formNode = this.node.querySelector(SELECTOR_FORM);
        this.listNode = this.node.querySelector(SELECTOR_LIST);
        this.responseEmailNode = this.node.querySelector(SELECTOR_RESPONSE_EMAIL);
        this.responseErrorNode = this.node.querySelector(SELECTOR_RESPONSE_ERROR);
        this.responseSuccessNode = this.node.querySelector(SELECTOR_RESPONSE_SUCCESS);
        this.submitNode = this.node.querySelector(SELECTOR_SUBMIT);
        this.wrapperNode = this.node.querySelector(SELECTOR_WRAPPER);

        this.facetsFormNode = document.querySelector(SELECTOR_FACETS_FORM);

        if (this.facetsFormNode) {
            this.distanceNode = this.facetsFormNode.querySelector(SELECTOR_DISTANCE);
            this.postalCodeCityNode = this.facetsFormNode.querySelector(SELECTOR_POSTAL_CODE_CITY);
            this.keywordsNode = this.facetsFormNode.querySelector(SELECTOR_KEYWORDS);
            this.modelKeywordsNode = this.facetsFormNode.querySelector(SELECTOR_MODEL_KEYWORDS);
        }
    }

    bindEventListeners() {
        this.node.addEventListener('click', e => this.handleClick(e));
    }

    initForm() {
        this.emailNode.addEventListener('change', () => this.validateEmail());
        this.formNode.addEventListener('submit', e => this.submit(e));

        this.feedback = JSON.parse(this.formNode.getAttribute(ATTR_FEEDBACK));
    }

    handleClick(e) {
        const { target } = e;
        const dataAttr = target.getAttribute('data-js');

        switch (dataAttr) {
            case JS_FORM_CANCEL:
                this.cancelForm();
                break;

            case JS_FORM_OPEN:
                this.openForm();
                break;

            case JS_FILTER_REMOVE:
                this.removeFilter(e.target);
                break;

            case JS_FILTERS_CLEAR:
                e.preventDefault();

                this.clearFilters();
                break;

            case JS_RESPONSE_CLOSE:
                this.closeResponse();
                break;

            // no default
        }
    }

    validateEmail() {
        const fieldWrapperNode = closest(this.emailNode, CLASS_FIELD_WRAPPER);
        const feedbackWrapperNode = fieldWrapperNode.querySelector(SELECTOR_FIELD_FEEDBACK);

        const pattern = this.emailNode.getAttribute('data-pattern');
        const maxLength = this.emailNode.getAttribute('maxlength');

        let isValid = true;
        let feedback;

        if (!passesRequired(this.emailNode)) {
            isValid = false;
            feedback = this.feedback.required;
        } else if (!passesPattern(this.emailNode)) {
            isValid = false;
            feedback = this.feedback.patterns[pattern];
        } else if (!passesMaxLength(this.emailNode)) {
            isValid = false;
            /* eslint-disable-next-line no-template-curly-in-string */
            feedback = this.feedback.maxLength.replace('${maxlength}', maxLength);
        }

        const state = isValid ? 'success' : 'error';

        fieldWrapperNode.setAttribute('data-state', state);

        feedbackWrapperNode.innerHTML = isValid ? '' : `<li>${feedback}</li>`;

        return isValid;
    }

    cancelForm() {
        this.toggle(this.formNode, false);
        this.toggle(this.actionsNode);
    }

    openForm() {
        this.toggle(this.formNode);
        this.toggle(this.actionsNode, false);

        Analytics.trackEvent(
            'srp.searchSubscription.openForm',
            'Custom Event',
            this.node.querySelectorAll(`.${CLASS_FILTER}`).length
        );
    }

    clearFilters() {
        this.pubsub.publish('clearFilters');
    }

    removeFilter(filterNode) {
        let filterId = filterNode.getAttribute('data-ref-id');

        if (filterId.startsWith('Brand')) {
            filterId = 'Brand';
        }

        this.pubsub.publish('removeFilter', { filterId });
    }

    submit(e) {
        e.preventDefault();

        if (this.validateEmail()) {
            this.submitNode.classList.add(CLASS_CTA_BUSY);
            this.submitNode.disabled = true;
            this.cancelNode.disabled = true;

            this.recaptcha &&
                this.recaptcha.submit(() => {
                    this.performSubmit();
                });
        }
    }

    performSubmit() {
        const url = this.formNode.getAttribute('action');
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-type': 'application/x-www-form-urlencoded'
            },
            body: this.getSerializedData()
        };

        fetch(url, requestOptions)
            .then(response => response.json())
            .then(data => this.processFormResponse(data))
            .catch(err => {
                this.submitNode.classList.remove(CLASS_CTA_BUSY);
                this.submitNode.disabled = false;
                this.cancelNode.disabled = false;

                console.error('Something went wrong!', err);
            });

        Analytics.trackEvent(
            'srp.searchSubscription.submitForm',
            'Custom Event',
            this.node.querySelectorAll(`.${CLASS_FILTER}`).length
        );
    }

    processFormResponse({ state, title, message }) {
        const responseNode = this.getResponseNodeByState(state);

        responseNode.querySelector('h2').innerHTML = title;
        responseNode.querySelector('p').innerHTML = message;

        this.toggle(this.formNode, false);
        this.toggle(responseNode, true, () => {
            if (state === 'success') {
                this.timeout = setTimeout(() => this.closeResponse(), 10000);
            }
        });

        this.submitNode.classList.remove(CLASS_CTA_BUSY);
        this.submitNode.disabled = false;
        this.cancelNode.disabled = false;
    }

    getResponseNodeByState(state) {
        return state === 'success' ? this.responseSuccessNode : this.responseErrorNode;
    }

    closeResponse() {
        const responseNode = !this.responseSuccessNode.hidden ? this.responseSuccessNode : this.responseErrorNode;

        animateCSS({
            node: responseNode,
            animationName: 'fadeOut',
            callback: () => {
                this.toggle(responseNode, false);
                this.toggle(this.actionsNode);
            }
        });
    }

    getSerializedData() {
        const formData = this.getFormData();
        const serializedData = serializeFormData(formData);

        return serializedData;
    }

    getFormData() {
        const formData = extractSearchFormData(this.formNode);

        if (this.facetsFormNode) {
            const facetsFormData = this.getFacetsFormData();

            return [...facetsFormData, ...formData];
        }

        return formData;
    }

    getFacetsFormData() {
        const isValidPostalCodeOrCity = this.validatePostalCodeOrCity();

        if (!isValidPostalCodeOrCity) {
            this.postalCodeCityNode.disabled = true;
            this.distanceNode.disabled = true;
        }

        if (this.keywordsNode) {
            if (!this.isValidSearch(this.keywordsNode)) {
                this.keywordsNode.disabled = true;
            }
        }

        if (this.modelKeywordsNode) {
            if (!this.isValidSearch(this.modelKeywordsNode)) {
                this.modelKeywordsNode.disabled = true;
            }
        }

        const facetsFormData = extractSearchFormData(this.facetsFormNode);

        this.postalCodeCityNode.disabled = false;
        this.distanceNode.disabled = false;

        if (this.keywordsNode) {
            this.keywordsNode.disabled = false;
        }

        if (this.modelKeywordsNode) {
            this.modelKeywordsNode.disabled = false;
        }

        return facetsFormData;
    }

    validatePostalCodeOrCity() {
        const postalCodeCityFieldWrapper = closest(this.postalCodeCityNode, 'input-text');
        const postalCodeCityFieldState = postalCodeCityFieldWrapper.getAttribute('data-state');

        return !(
            (postalCodeCityFieldState && postalCodeCityFieldState === 'error') ||
            this.postalCodeCityNode.value.length === 0
        );
    }

    isValidSearch(searchNode) {
        const searchFieldInput = closest(searchNode, 'input-text');

        return validator.validateField(searchFieldInput).isValid;
    }

    processResponse(filters) {
        this.emptyFilterCloud();
        this.cancelForm();

        this.toggle(this.responseErrorNode, false);
        this.toggle(this.responseSuccessNode, false);
        this.toggle(this.responseEmailNode, false);

        this.toggle(this.btnSaveNode);
        this.toggle(this.actionsNode);

        filters.forEach(filter => this.renderFilter(filter));

        this.toggle(this.wrapperNode, filters.length > 0);
    }

    renderFilter(filter) {
        this.listNode.appendChild(this.createFilterNode(filter));
    }

    emptyFilterCloud() {
        this.listNode.innerHTML = '';
    }

    toggle(node, toggle = true, callback) {
        if (!node || (node.hidden && !toggle) || (!node.hidden && toggle)) {
            return;
        }

        node.hidden = !toggle;

        if (toggle) {
            animateCSS({
                node,
                animationName: 'fadeIn'
            });
        }

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (typeof callback === 'function') {
            callback();
        }
    }

    createFilterNode({ id, label }) {
        const filterTitlePrefix = this.node.getAttribute('data-filter-cloud-title-prefix');

        /* eslint-disable max-len */
        const filterNode = parseHtmlToDom(`
            <li class="filter-cloud__list-item">
                <div class="filter-cloud__filter" title="${filterTitlePrefix} ${label}" data-js="filter-remove" data-ref-id="${id}">
                    <span class="filter-cloud__filter-text">${label}</span>
                    <svg class="icon-close" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
                        <path d="M7.426 6l4.279-4.278A1.008 1.008 0 1 0 10.278.295L6 4.574 1.721.295A1.008 1.008 0 1 0 .295 1.722L4.574 6 .295 10.278a1.009 1.009 0 0 0 1.426 1.427L6 7.426l4.278 4.279a1.008 1.008 0 1 0 1.427-1.427L7.426 6z"/>
                    </svg>
                </div>
            </li>
        `);
        /* eslint-enable max-len */

        return filterNode;
    }

    determineFilterContainerWidth() {
        const viewportWidth = getViewport().width;
        const filterNodes = this.node.querySelectorAll(`.${CLASS_FILTER}`);

        if (viewportWidth >= breakpoints.tablet.from || filterNodes.length === 0) {
            this.listNode.removeAttribute('style');

            return;
        }

        let totalWidth = 0;
        let maxNodeWidth = 0;

        const listItemMargin = {
            left: parseFloat(getComputedStyle(filterNodes[0], 'margin-left')),
            right: parseFloat(getComputedStyle(filterNodes[0], 'margin-right'))
        };

        filterNodes.forEach(filterNode => {
            const nodeWidth = filterNode.getBoundingClientRect().width;

            maxNodeWidth = Math.max(nodeWidth, maxNodeWidth);
            totalWidth += nodeWidth + listItemMargin.left + listItemMargin.right;
        });

        const requiredWidth = totalWidth / 2 + maxNodeWidth / 2;
        const currentWidth = this.listNode.getBoundingClientRect().width;

        if (requiredWidth > currentWidth) {
            this.listNode.style.width = `${requiredWidth}px`;
        }
    }
}
